/**
 * ChanceMethods - 获取抽奖机会方式组件
 *
 * 展示用户获取额外抽奖机会的多种方式(如分享、积分兑换、推荐等)。
 * 基于 Figma 设计稿实现响应式布局。
 *
 * Figma 设计稿: https://www.figma.com/design/DhWsPamqTM76eMHS0aIQqw
 * 设计节点: 498:6185 (Chance Methods section)
 *
 * @module ChanceMethods
 * @date 2025-12-17
 */

'use client'

import React from 'react'
import { cn } from '../../helpers/index.js'
import type { ChanceMethod, ChanceMethodsProps, UserData } from './types.js'
import { Button, Heading } from '../../components/index.js'

// ============================================================================
// ChanceMethods 组件
// ============================================================================

/**
 * ChanceMethods - 获取抽奖机会方式组件
 *
 * 特性:
 * - 展示多种获取机会的方式(分享、积分、推荐等)
 * - 每种方式显示标题、描述、Chance +1 徽章和行动按钮
 * - 支持禁用状态
 * - 响应式布局
 */
export const ChanceMethods: React.FC<ChanceMethodsProps> = ({
  theme,
  methods,
  title = 'Want more chances to win?',
  subtitle,
  footerNote,
  className,
  userData,
  onOpenShareModal,
  chanceBadgeText = 'Chance +1',
  completedText = 'Completed',
  usedText = 'Used',
  loadingText = 'Loading...',
}) => {
  // 防御性编程:处理 undefined 或空数组
  if (!methods || methods.length === 0) {
    return null
  }

  return (
    <div
      className={cn(
        'chance-methods-container',
        'flex flex-col overflow-y-auto',
        'p-[16px]',
        'desktop:px-[24px] desktop:pt-[24px]',
        'lg-desktop:px-[32px]',
        'gap-[16px]', // 默认间距
        'lg-desktop:gap-[16px]', // lg-desktop: Figma 设计稿间距
        theme === 'dark' ? 'aiui-dark' : '',
        className
      )}
    >
      {/* 标题区域 */}
      <div className="chance-methods-header flex flex-col gap-[4px]">
        <Heading size={2} className={cn('chance-methods-title', 'text-info-primary tracking-[-0.32px]')}>
          {title}
        </Heading>

        {subtitle && (
          <p
            className={cn(
              'chance-methods-subtitle',
              'text-[14px] font-semibold leading-[1.4]',
              'lg-desktop:text-[18px] desktop:text-[16px]',
              theme === 'dark' ? 'text-[#8A8D92]' : 'text-[#4A4C56]'
            )}
          >
            {subtitle}
          </p>
        )}
      </div>

      {/* 获取机会方式列表 */}
      <div className="chance-methods-list flex grow flex-col gap-[16px]">
        {methods.map(method => (
          <ChanceMethodItem
            key={method.methodKey}
            method={method}
            userData={userData}
            onOpenShareModal={onOpenShareModal}
            chanceBadgeText={chanceBadgeText}
            completedText={completedText}
            usedText={usedText}
            loadingText={loadingText}
          />
        ))}
      </div>

      {/* 底部提示 */}
      {footerNote && (
        <p
          className={cn(
            'chance-methods-footer',
            'font-semibold',
            'text-[14px] leading-[1.4] ',
            'desktop:pt-[24px] pt-[16px]',
            theme === 'dark' ? 'text-[#75787F]' : 'text-[#767880]'
          )}
        >
          {footerNote}
        </p>
      )}
    </div>
  )
}

// ============================================================================
// ChanceMethodItem 子组件 - 单个获取机会方式卡片
// ============================================================================

interface ChanceMethodItemProps {
  method: ChanceMethod
  userData?: UserData
  onOpenShareModal?: () => void
  chanceBadgeText: string
  completedText: string
  usedText: string
  loadingText: string
}

/**
 * ChanceMethodItem - 单个获取机会方式卡片
 *
 * 展示方式名称、描述、Chance +1 徽章和行动按钮。
 * 支持三种状态：pending（未完成）、completed（已完成）、used（已使用）
 */
const ChanceMethodItem: React.FC<ChanceMethodItemProps> = ({
  method,
  userData,
  onOpenShareModal,
  chanceBadgeText,
  completedText,
  usedText,
  loadingText,
}) => {
  const status = method.status || 'pending'
  const isLoading = method.loading || false
  const isDisabledOrCompleted = method.disabled || status === 'completed' || status === 'used' || isLoading

  // 处理按钮点击
  const handleClick = () => {
    // 检查登录状态
    if (!userData?.isLoggedIn) {
      // 未登录时，只调用登录回调，不打开分享弹窗
      method.onLoginRequired?.()
      return
    }

    // 已登录时，执行正常逻辑
    // 如果配置了 openShareModal，则打开分享弹窗
    if (method.openShareModal && onOpenShareModal) {
      onOpenShareModal()
    }
    // 执行原有的 onClick 回调
    method.onClick()
  }

  // 状态文本映射
  const statusTextMap = {
    pending: isLoading ? loadingText : method?.buttonText || 'Redeem now',
    completed: completedText,
    used: usedText,
  }

  return (
    <div
      className={cn(
        'chance-method-item',
        'flex flex-col items-start gap-[8px]',
        'tablet:flex-row tablet:items-center tablet:gap-4',
        'transition-all',
        {
          'opacity-60': isDisabledOrCompleted,
        }
      )}
    >
      {/* 左侧:方式信息 */}
      <div
        className={cn(
          'chance-method-info',
          'flex flex-1 flex-col gap-[4px]',
          'min-w-0' // 确保文本可以被截断
        )}
      >
        {/* 标题 + Chance +1 徽章 */}
        <div className="flex flex-wrap items-center gap-[4px]">
          <p
            className={cn(
              'chance-method-title',
              'text-info-primary',
              'text-[14px] font-[700] leading-[1.4] ',
              'lg-desktop:text-[18px] desktop:text-[16px]'
            )}
          >
            {method.title}
          </p>

          <span
            className={cn(
              'chance-badge',
              'text-[14px] leading-[1.4] ',
              'lg-desktop:text-[18px] desktop:text-[16px]',
              'font-[700]',
              'shrink-0',
              {
                'text-[#ff6b35]': status === 'pending',
                'text-[#4caf50]': status === 'completed',
                'text-[#75787F]': status === 'used',
              }
            )}
          >
            {chanceBadgeText}
          </span>
        </div>

        {/* 描述文本 */}
        <p
          className={cn(
            'chance-method-description',
            'text-info-primary',
            'text-[14px] leading-[1.4] ',
            'lg-desktop:text-[18px] desktop:text-[16px]',
            'font-[700]'
          )}
        >
          {method.description}
        </p>
      </div>

      {/* 右侧:行动按钮 */}
      <div className="chance-method-action shrink-0">
        <Button
          onClick={handleClick}
          disabled={isDisabledOrCompleted}
          className={cn('chance-button', 'lg-desktop:min-w-[162px] min-w-[132px]', {
            'bg-[#4caf50]': status === 'completed',
            'bg-[#75787F]': status === 'used',
          })}
        >
          {statusTextMap[status]}
        </Button>
      </div>
    </div>
  )
}

// ============================================================================
// 默认导出
// ============================================================================

export default ChanceMethods
